import {BookOpenIcon, EnvelopeIcon, LifebuoyIcon, PhoneIcon} from "@heroicons/react/20/solid";
import {PropsWithChildren, ReactNode, useId} from "react";
import {AdminPage} from "../components/AdminPage";
import {OtsLink} from "../components/OtsLink";
import {OtsResourceCard} from "../components/OtsResourceCard";
import {OtsBrand, OtsColorSchemePreference, OtsThemeRoot} from "../theme/OtsTheme";
import {OtsMobileApp, otsMobileApps} from "./ots-mobile-apps";
import {OtsMobileAppCard} from "./OtsMobileAppCard";
import {OtsSupportContact, otsResourceUrls, otsSupportContact, toTelHref} from "./ots-support";

export type HelpSupportPageProps = PropsWithChildren<{
  brand?: OtsBrand;
  /** Light or dark. Defaults to "system": the host page's theme, else the operating system's. */
  colorScheme?: OtsColorSchemePreference;
  documentationUrl?: string;
  websiteUrl?: string;
  /** Defaults to the OTS support line and address; leave a field out to hide that row. */
  contact?: OtsSupportContact;
  /** Apps to offer. Pass an empty list to leave the section out. */
  apps?: readonly OtsMobileApp[];
  className?: string;
}>;

/**
 * Where to get help: documentation, the support team, and the mobile apps. Host-specific sections
 * go in `children`, after the standard ones.
 */
export const HelpSupportPage = ({
  brand,
  colorScheme,
  documentationUrl = otsResourceUrls.documentation,
  websiteUrl = otsResourceUrls.website,
  contact = otsSupportContact,
  apps = otsMobileApps,
  className,
  children
}: HelpSupportPageProps) => {
  const appsHeadingId = useId();

  return (
    // Fills the host panel with the themed background. GWT's bridge root is display: contents, so this
    // sits directly in a scrolling container, and without it the background would stop at the content.
    <OtsThemeRoot
      brand={brand}
      colorScheme={colorScheme}
      defaultColorScheme="system"
      className="min-h-full bg-[var(--ots-background)]"
      data-testid="help-support-tw">
      <AdminPage
        className={className}
        eyebrow="Support"
        title="Help & Support"
        subtitle="Find product documentation, contact the OTS support team, or download an OTS mobile app.">
        {/* Laid out by its own width, not the viewport's: GWT mounts this in panels narrower than the window. */}
        <div className="@container mt-8 space-y-8">
          <div className="grid gap-6 @2xl:grid-cols-2">
            <OtsResourceCard
              icon={<BookOpenIcon/>}
              eyebrow="Documentation"
              title="OTS product documentation"
              description="Guides and reference material for the OTS platform and mobile apps."
              footer={(
                <>
                  <OtsLink href={documentationUrl}>Open documentation</OtsLink>
                  <OtsLink href={websiteUrl}>Visit the OTS website</OtsLink>
                </>
              )}/>

            {(contact.phone || contact.email) && (
              <OtsResourceCard
                icon={<LifebuoyIcon/>}
                eyebrow="Contact support"
                title="Talk with the OTS team"
                description="Reach the support team by phone or email.">
                <ul className="space-y-4">
                  {contact.phone && (
                    <ContactRow icon={<PhoneIcon/>} label="Phone">
                      <OtsLink href={toTelHref(contact)}>{contact.phone}</OtsLink>
                    </ContactRow>
                  )}
                  {contact.email && (
                    <ContactRow icon={<EnvelopeIcon/>} label="Email">
                      <OtsLink href={`mailto:${contact.email}`} className="break-all">{contact.email}</OtsLink>
                    </ContactRow>
                  )}
                </ul>
              </OtsResourceCard>
            )}
          </div>

          {apps.length > 0 && (
            <section aria-labelledby={appsHeadingId}>
              <h2 id={appsHeadingId} className="font-[family-name:var(--ots-font-display)] text-lg font-semibold text-[var(--ots-secondary)]">
                Mobile apps
              </h2>
              <p className="mt-1 text-sm text-[var(--ots-text-muted)]">Choose your app and device platform.</p>
              <ul className="mt-4 grid gap-4 @xl:grid-cols-2">
                {apps.map((app) => (
                  <li key={app.id}>
                    <OtsMobileAppCard app={app} className="h-full"/>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {children}
        </div>
      </AdminPage>
    </OtsThemeRoot>
  );
};

const ContactRow = ({icon, label, children}: PropsWithChildren<{icon: ReactNode; label: string}>) => (
  <li className="flex items-start gap-3">
    <span aria-hidden="true" className="mt-0.5 text-[var(--ots-text-muted)] [&>svg]:size-4">{icon}</span>
    <div className="min-w-0">
      <p className="text-xs font-medium text-[var(--ots-text-muted)]">{label}</p>
      <p className="mt-0.5 text-sm">{children}</p>
    </div>
  </li>
);
